import { FC } from 'react'
import { TextField, Button, Box, Stack, Card as MuiCard, Typography, Divider } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'

import PasswordField from '@components/PasswordField'
import { useObtain } from '@services/token/token.hooks'
import MicrosoftLoginButton from '@components/MicrosoftLoginButton'
import BackgroundImage from '@assets/bg.jpg'
import LogoImage from '@assets/logo.png'
import { styled } from '@mui/material/styles'

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  backgroundImage: `url(${BackgroundImage})`,
  flex: 2,
  minHeight: '100vh',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4)
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage: 'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage: 'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))'
    })
  }
}))

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px'
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px'
  })
}))

const Login: FC = () => {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const navigation = useNavigate()
  const obtain = useObtain({
    onError: () => {
      enqueueSnackbar(
        formatMessage({
          id: 'validation.obtain',
          defaultMessage: 'Błąd logowania. Podano nieprawidłowy login lub hasło.'
        }),
        {
          variant: 'error'
        }
      )
    }
  })

  const form = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(
          formatMessage({
            id: 'validation.email',
            defaultMessage: 'Niepoprawny adres email'
          })
        )
        .required(
          formatMessage({
            id: 'validation.required',
            defaultMessage: 'Wymagane'
          })
        ),
      password: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      )
    }),
    onSubmit: async (values) => {
      await obtain.mutate(values)
    }
  })

  return (
    <SignInContainer>
      <Card variant='outlined'>
        <Typography
          component='h4'
          variant='h4'
          sx={{
            width: '100%',
            fontSize: '16px',
            mb: -2,
            fontWeight: 700,
            color: '#1F1F71',
            textAlign: 'center'
          }}
        >
          Helpdesk
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <img src={LogoImage} alt='Logo' style={{ width: '180px', height: 'auto' }} />
        </Box>
        <Typography
          component='h1'
          variant='h4'
          sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)', mb: 4, fontWeight: 700 }}
        >
          Logowanie
        </Typography>
        <form onSubmit={form.handleSubmit}>
          <TextField
            name='email'
            value={form.values.email}
            onChange={form.handleChange}
            fullWidth
            placeholder={formatMessage({
              id: 'login.form.email',
              defaultMessage: 'Email'
            })}
            label={formatMessage({
              id: 'login.form.email',
              defaultMessage: 'Email'
            })}
            disabled={obtain.isPending}
            sx={{ mb: 3 }}
            error={!!form.errors.email}
            helperText={!!form.errors.email && form.errors.email}
          />
          <PasswordField
            name='password'
            value={form.values.password}
            onChange={form.handleChange}
            fullWidth
            sx={{ mb: 6 }}
            label={formatMessage({
              id: 'login.form.password',
              defaultMessage: 'Hasło'
            })}
            disabled={obtain.isPending}
            error={!!form.errors.password}
            helperText={!!form.errors.password && form.errors.password}
          />
          <Button
            fullWidth
            size='large'
            variant='contained'
            sx={{ mb: 2 }}
            type='submit'
            loading={obtain.isPending}
          >
            {formatMessage({
              id: 'login.form.submit',
              defaultMessage: 'ZALOGUJ SIĘ'
            })}
          </Button>
          <Divider sx={{ my: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              LUB
            </Typography>
          </Divider>
          <MicrosoftLoginButton />
          <Button
            onClick={() => navigation('/auth/sign-up')}
            color='secondary'
            fullWidth
            size='large'
            variant='text'
            sx={{ textTransform: 'none', fontWeight: '400', display: 'none' }}
          >
            {formatMessage({
              id: 'login.form.signup',
              defaultMessage: 'Nie masz konta? Zarejestruj się'
            })}
          </Button>
          <Button
            onClick={() => navigation('/auth/reset-password')}
            color='secondary'
            fullWidth
            size='large'
            variant='text'
            sx={{
              mb: 1,
              textTransform: 'none',
              fontWeight: '400',
              display: 'none'
            }}
          >
            {formatMessage({
              id: 'login.form.resetPassword',
              defaultMessage: 'Nie pamiętam hasła'
            })}
          </Button>
        </form>
      </Card>
    </SignInContainer>
  )

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#fff',
        [theme.breakpoints.down('md')]: {
          position: 'absolute',
          width: '100%',
          left: 0,
          top: 0
        }
      })}
    >
      <Box
        sx={(theme) => ({
          p: 4,
          flex: 1,
          [theme.breakpoints.down('md')]: {
            zIndex: 1,
            backgroundColor: '#fff'
          }
        })}
      ></Box>
      <Box
        sx={(theme) => ({
          backgroundImage: `url(${BackgroundImage})`,
          flex: 2,
          minHeight: '100vh',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          [theme.breakpoints.down('md')]: {
            position: 'absolute',
            left: 0,
            right: 0,
            zIndex: 0
          }
        })}
      ></Box>
    </Box>
  )
}

export default Login
